import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { ConciergeHeaderWidget } from "@brandclub/bc-widgets";
import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { toast, Toaster } from "sonner";
import { useBrandAppConfig } from "../api/cache/appConfigQueryOptions.js";
import { getHeadersWithUniqueVisitorId } from "../Auth/index.js";
import { fetchProductByStacklineSku } from "./api.js";
import { ConciergeChatAreaV2 } from "./components/ConciergeChatAreaV2.js";
import DevOptionsPanel from "./components/DevOptionsPanel.js";
import { useDevOptions } from "./hooks/useDevOptions.js";
import { PinnedProductContext } from "./components/PinnedProductContext.js";
import { useConciergeContext } from "./ConciergeContextProvider.js";
import { useAutoScroll } from "./hooks/useAutoScroll.js";
import { useConciergeTracking } from "./hooks/useConciergeTracking.js";
import { useConversationLoader } from "./hooks/useConversationLoader.js";
import { usePageContext } from "./hooks/usePageContext.js";
import { useStreamingSearch } from "./hooks/useStreamingSearch.js";
import { useConciergePendingQuery } from "./stores/useConciergePendingQuery.js";
import { wrapSearchWithProductContext } from "./utils/wrapSearchWithProductContext.js";
const ConciergeWidgetChatContent = ({
  pageContext: providedPageContext,
  widgetState,
  showMaximize = true,
  isMaximized = false,
  onMaximize: _onMaximize,
  onMinimize,
  onVariantSelect,
  title,
  initialQuery,
  initialQueryStacklineSku,
  onInitialQueryProcessed,
  brandId: propBrandId,
  conciergePageUrl: propConciergePageUrl
}) => {
  var _a, _b;
  const conversationState = useConciergeContext();
  const { data: config } = useBrandAppConfig(conversationState.apiBaseUrl);
  const params = new URLSearchParams(window.location.search);
  const brandId = (propBrandId == null ? void 0 : propBrandId.toString()) || params.get("brandId") || ((_b = (_a = config == null ? void 0 : config.brandIds) == null ? void 0 : _a[0]) == null ? void 0 : _b.toString()) || null;
  const appConfigId = params.get("appConfigId") || (config == null ? void 0 : config.appConfigId) || null;
  const [searchInputValue, setSearchInputValue] = useState("");
  const devOpts = useDevOptions();
  const getConciergePageUrl = useCallback(
    (opts = {}) => {
      const base = propConciergePageUrl ?? `${window.location.origin}/concierge`;
      const url = new URL(base);
      if (brandId) url.searchParams.set("brandId", brandId);
      if (opts.conversationId) url.searchParams.set("conversationId", opts.conversationId);
      return url.toString();
    },
    [propConciergePageUrl, brandId]
  );
  const handleHistoryClick = useCallback(() => {
    window.open(getConciergePageUrl(), "_blank", "noopener,noreferrer");
  }, [getConciergePageUrl]);
  const handleMaximizeClick = useCallback(() => {
    const url = getConciergePageUrl({ conversationId: conversationState.conversationId });
    window.open(url, "_blank", "noopener,noreferrer");
  }, [getConciergePageUrl, conversationState.conversationId]);
  const cancelStreamRef = useRef(() => {
  });
  const resetConversationState = useCallback(() => {
    cancelStreamRef.current();
    conversationState.setIsActivelyStreaming(false);
    conversationState.clearStreamingState();
    conversationState.setConversationId(null);
    conversationState.setConversationHistory([]);
    conversationState.setProductMetadataMap(/* @__PURE__ */ new Map());
    conversationState.setFollowUpQuestions([]);
    conversationState.setPreviewSearchQueries([]);
    conversationState.setPreviewQueryProducts([]);
    conversationState.setConversationPageContext(null);
  }, [conversationState]);
  const hookPageContext = usePageContext();
  const pageContext = providedPageContext ?? hookPageContext;
  const previousPageContextRef = useRef(null);
  const isPageContextDifferent = useCallback((a, b) => {
    if (a.pageType !== b.pageType) return true;
    if (a.pageType === "pdp" && b.pageType === "pdp" && a.stacklineSku !== b.stacklineSku) return true;
    return false;
  }, []);
  useEffect(() => {
    const hasConversation = conversationState.conversationId != null || conversationState.conversationHistory.length > 0;
    if (hasConversation && conversationState.conversationPageContext == null) {
      conversationState.setConversationPageContext(pageContext);
    }
  }, [
    pageContext,
    conversationState.conversationId,
    conversationState.conversationHistory.length,
    conversationState.conversationPageContext,
    conversationState.setConversationPageContext
  ]);
  useEffect(() => {
    const previousContext = previousPageContextRef.current;
    const storedConversationContext = conversationState.conversationPageContext;
    if (previousContext === null) {
      previousPageContextRef.current = pageContext;
      const hasConversation = conversationState.conversationId != null || conversationState.conversationHistory.length > 0;
      if (hasConversation && storedConversationContext != null && isPageContextDifferent(pageContext, storedConversationContext)) {
        resetConversationState();
      }
      return;
    }
    const hasPageContextChanged = isPageContextDifferent(previousContext, pageContext);
    if (hasPageContextChanged) {
      resetConversationState();
    }
    previousPageContextRef.current = pageContext;
  }, [pageContext, conversationState, isPageContextDifferent, resetConversationState]);
  const pendingAccessPointRef = useRef(null);
  const isNewConversationRef = useRef(true);
  useEffect(() => {
    isNewConversationRef.current = conversationState.conversationHistory.length === 0;
  }, [conversationState.conversationHistory.length]);
  const { trackConversationStart, trackPageContext } = useConciergeTracking({
    conversationId: conversationState.conversationId,
    pageContext
  });
  const previousConversationIdRef = useRef(null);
  useEffect(() => {
    const currentId = conversationState.conversationId;
    const previousId = previousConversationIdRef.current;
    if (currentId && currentId !== previousId && pendingAccessPointRef.current) {
      const { accessPoint, query } = pendingAccessPointRef.current;
      const isNew = previousId === null;
      trackConversationStart(accessPoint, query, isNew);
      pendingAccessPointRef.current = null;
    }
    previousConversationIdRef.current = currentId;
  }, [conversationState.conversationId, trackConversationStart]);
  const handleTrackAccessPoint = useCallback(
    (accessPoint, query) => {
      pendingAccessPointRef.current = { accessPoint, query };
      if (conversationState.conversationId) {
        const isNew = isNewConversationRef.current;
        trackConversationStart(accessPoint, query, isNew);
        pendingAccessPointRef.current = null;
      }
    },
    [conversationState.conversationId, trackConversationStart]
  );
  const { isOpen } = widgetState;
  const { pendingQuery, pendingStacklineSku, pendingQueryId, clearPendingQuery } = useConciergePendingQuery();
  const lastProcessedQueryIdRef = useRef(null);
  const { shouldScrollOnNextMessageRef, scrollToBottom } = useAutoScroll(
    conversationState.conversationHistory.length,
    conversationState.isActivelyStreaming,
    conversationState.streamingAIResponse,
    conversationState.conversationHistory.length === 0
  );
  const handleStreamError = useCallback(
    (errorType, originalMessage) => {
      setSearchInputValue(originalMessage);
      const errorMessages = {
        rate_limit: "Too many requests. Please wait a moment before trying again.",
        network: "Network error. Please check your connection and try again.",
        server: "Something went wrong on our end. Please try again.",
        unknown: "Something went wrong. Please try again."
      };
      toast.error(errorMessages[errorType]);
    },
    []
  );
  const { performSearch, cancelStream } = useStreamingSearch({
    brandId,
    appConfigId,
    conversationState,
    scrollToBottom,
    shouldScrollOnNextMessageRef,
    onStreamError: handleStreamError,
    devOptions: devOpts.devOptions
  });
  cancelStreamRef.current = cancelStream;
  const handleSearch = useCallback(
    async (searchTerm, imageData, imageMimeType) => {
      setSearchInputValue("");
      const isFirstMessage = conversationState.conversationHistory.length === 0;
      const searchTermWithContext = isFirstMessage ? wrapSearchWithProductContext(searchTerm, pageContext) : searchTerm;
      await performSearch(searchTermWithContext, imageData, imageMimeType);
    },
    [pageContext, performSearch, conversationState.conversationHistory.length]
  );
  const handleUpdateFeedback = (messageIndex, feedback) => {
    conversationState.setConversationHistory((prev) => {
      return prev.map((msg, idx) => {
        if (idx === messageIndex) {
          return {
            ...msg,
            feedback: feedback || void 0
          };
        }
        return msg;
      });
    });
  };
  const { isLoadingConversationQuery } = useConversationLoader({
    conversationId: conversationState.conversationId,
    isActivelyStreaming: conversationState.isActivelyStreaming,
    conversationHistoryLength: conversationState.conversationHistory.length,
    apiBaseUrl: conversationState.apiBaseUrl,
    onConversationLoaded: (messages, productMap, surveyMap, followUpQuestions, previewSearchQueries, previewQueryProducts, sharingInfo) => {
      conversationState.setProductMetadataMap(productMap);
      conversationState.setSurveyMetadataMap(surveyMap);
      conversationState.setConversationHistory(messages);
      conversationState.setFollowUpQuestions(followUpQuestions);
      conversationState.setPreviewSearchQueries(previewSearchQueries);
      conversationState.setPreviewQueryProducts(previewQueryProducts);
      if (sharingInfo) {
        conversationState.setIsSharedView(sharingInfo.isSharedView ?? false);
        conversationState.setSharedByUserName(sharingInfo.sharedByUserName);
        conversationState.setSourceConversationId(sharingInfo.sourceConversationId);
        conversationState.setSourceCheckpointIndex(sharingInfo.sourceCheckpointIndex);
      } else {
        conversationState.setIsSharedView(false);
        conversationState.setSharedByUserName(void 0);
        conversationState.setSourceConversationId(void 0);
        conversationState.setSourceCheckpointIndex(void 0);
      }
    },
    onConversationNotFound: () => {
      conversationState.setConversationId(null);
      conversationState.setConversationPageContext(null);
    }
  });
  useEffect(() => {
    if (!pendingQuery || !pendingQueryId || !isOpen) {
      return;
    }
    if (!performSearch || typeof performSearch !== "function") {
      return;
    }
    if (lastProcessedQueryIdRef.current === pendingQueryId) {
      return;
    }
    lastProcessedQueryIdRef.current = pendingQueryId;
    const queryToSend = pendingQuery;
    const stacklineSkuToUse = pendingStacklineSku;
    clearPendingQuery();
    const isFirstMessage = conversationState.conversationHistory.length === 0;
    let messageToSend = queryToSend;
    if (isFirstMessage && stacklineSkuToUse) {
      const productPageContext = {
        pageType: "pdp",
        stacklineSku: stacklineSkuToUse
      };
      messageToSend = wrapSearchWithProductContext(queryToSend, productPageContext);
    }
    const timeoutId = setTimeout(() => {
      if (!isOpen || !performSearch || typeof performSearch !== "function") {
        lastProcessedQueryIdRef.current = null;
        return;
      }
      try {
        handleTrackAccessPoint("conversation_starter", queryToSend);
        performSearch(messageToSend);
      } catch (error) {
        console.error("Error processing pending query:", error);
        toast.error("Failed to process search. Please try again.");
        lastProcessedQueryIdRef.current = null;
      }
    }, 100);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [
    isOpen,
    pendingQuery,
    pendingQueryId,
    pendingStacklineSku,
    performSearch,
    clearPendingQuery,
    handleTrackAccessPoint
  ]);
  const wrappedOnSignIn = useCallback(() => {
    if (!conversationState.onSignIn) return;
    const lastUserMessage = [...conversationState.conversationHistory].reverse().find((m) => m.role === "user");
    conversationState.onSignIn(lastUserMessage == null ? void 0 : lastUserMessage.content);
  }, [conversationState]);
  const hasCheckedPendingLoginRef = useRef(false);
  useEffect(() => {
    if (hasCheckedPendingLoginRef.current) return;
    const pendingMessage = sessionStorage.getItem("concierge_resend");
    if (!pendingMessage) {
      hasCheckedPendingLoginRef.current = true;
      return;
    }
    if (!conversationState.isSignedIn) return;
    hasCheckedPendingLoginRef.current = true;
    sessionStorage.removeItem("concierge_resend");
    conversationState.setConversationHistory([]);
    conversationState.setConversationId(null);
    conversationState.clearStreamingState();
    setTimeout(() => performSearch(pendingMessage), 500);
  }, [performSearch, conversationState]);
  const lastProcessedInitialQueryRef = useRef(null);
  const isProcessingInitialQueryRef = useRef(false);
  useEffect(() => {
    if (!initialQuery || !isOpen) {
      return;
    }
    if (!performSearch || typeof performSearch !== "function") {
      return;
    }
    if (lastProcessedInitialQueryRef.current === initialQuery) {
      return;
    }
    if (isProcessingInitialQueryRef.current) {
      return;
    }
    isProcessingInitialQueryRef.current = true;
    const queryToSend = initialQuery;
    const stacklineSkuToUse = initialQueryStacklineSku;
    const isFirstMessage = conversationState.conversationHistory.length === 0;
    let messageToSend = queryToSend;
    if (isFirstMessage && stacklineSkuToUse) {
      const productPageContext = {
        pageType: "pdp",
        stacklineSku: stacklineSkuToUse
      };
      messageToSend = wrapSearchWithProductContext(queryToSend, productPageContext);
    }
    const timeoutId = setTimeout(() => {
      if (!isOpen || !performSearch || typeof performSearch !== "function") {
        isProcessingInitialQueryRef.current = false;
        return;
      }
      try {
        handleTrackAccessPoint("conversation_starter", queryToSend);
        performSearch(messageToSend);
        lastProcessedInitialQueryRef.current = queryToSend;
        onInitialQueryProcessed == null ? void 0 : onInitialQueryProcessed();
      } catch (error) {
        console.error("Error processing initial query:", error);
        toast.error("Failed to process search. Please try again.");
      } finally {
        isProcessingInitialQueryRef.current = false;
      }
    }, 100);
    return () => {
      clearTimeout(timeoutId);
      isProcessingInitialQueryRef.current = false;
    };
  }, [isOpen, initialQuery, initialQueryStacklineSku, performSearch, onInitialQueryProcessed, handleTrackAccessPoint]);
  const [pinnedProduct, setPinnedProduct] = useState(null);
  const [isPinnedProductLoading, setIsPinnedProductLoading] = useState(false);
  useEffect(() => {
    const currentPageType = pageContext == null ? void 0 : pageContext.pageType;
    const currentStacklineSku = currentPageType === "pdp" ? pageContext == null ? void 0 : pageContext.stacklineSku : void 0;
    setPinnedProduct(null);
    setIsPinnedProductLoading(false);
    if (!currentStacklineSku) {
      return;
    }
    const existingProduct = conversationState.productMetadataMap.get(currentStacklineSku);
    if (existingProduct) {
      setPinnedProduct(existingProduct);
      return;
    }
    let isCancelled = false;
    const fetchProduct = async () => {
      setIsPinnedProductLoading(true);
      try {
        const headers = await getHeadersWithUniqueVisitorId();
        const product = await fetchProductByStacklineSku({
          stacklineSku: currentStacklineSku,
          headersToInject: headers,
          apiBaseUrl: conversationState.apiBaseUrl
        });
        if (!isCancelled && product) {
          setPinnedProduct(product);
          conversationState.setProductMetadataMap((prev) => {
            const newMap = new Map(prev);
            newMap.set(currentStacklineSku, product);
            return newMap;
          });
        }
      } catch (error) {
        if (!isCancelled) {
          console.error("Error fetching pinned product:", error);
        }
      } finally {
        if (!isCancelled) {
          setIsPinnedProductLoading(false);
        }
      }
    };
    fetchProduct();
    return () => {
      isCancelled = true;
    };
  }, [pageContext == null ? void 0 : pageContext.pageType, pageContext == null ? void 0 : pageContext.stacklineSku, conversationState.apiBaseUrl]);
  const pinnedStacklineSku = (pageContext == null ? void 0 : pageContext.pageType) === "pdp" ? pageContext.stacklineSku : void 0;
  const filteredStreamingFeaturedProduct = useMemo(() => {
    if (!conversationState.streamingFeaturedProduct || !pinnedStacklineSku) {
      return conversationState.streamingFeaturedProduct;
    }
    if (conversationState.streamingFeaturedProduct.product.stacklineSku === pinnedStacklineSku) {
      return null;
    }
    return conversationState.streamingFeaturedProduct;
  }, [conversationState.streamingFeaturedProduct, pinnedStacklineSku]);
  const filteredConversationHistory = useMemo(() => {
    if (!pinnedStacklineSku) {
      return conversationState.conversationHistory;
    }
    const pinnedProductTagRegex = new RegExp(
      `<product\\s+featured\\s*=\\s*"true"\\s+stacklineSku\\s*=\\s*"${pinnedStacklineSku}"(?:\\s+question\\s*=\\s*"[^"]*")?\\s*/?>`,
      "g"
    );
    return conversationState.conversationHistory.map((message) => {
      var _a2, _b2;
      let updatedMessage = message;
      if ("featuredProduct" in message && ((_b2 = (_a2 = message.featuredProduct) == null ? void 0 : _a2.product) == null ? void 0 : _b2.stacklineSku) === pinnedStacklineSku) {
        const { featuredProduct: _, ...rest } = message;
        updatedMessage = rest;
      }
      if (updatedMessage.content && pinnedProductTagRegex.test(updatedMessage.content)) {
        pinnedProductTagRegex.lastIndex = 0;
        const filteredContent = updatedMessage.content.replace(pinnedProductTagRegex, "").trim();
        updatedMessage = { ...updatedMessage, content: filteredContent };
      }
      return updatedMessage;
    });
  }, [conversationState.conversationHistory, pinnedStacklineSku]);
  const filteredStreamingAIResponse = useMemo(() => {
    if (!pinnedStacklineSku || !conversationState.streamingAIResponse) {
      return conversationState.streamingAIResponse;
    }
    const pinnedProductTagRegex = new RegExp(
      `<product\\s+featured\\s*=\\s*"true"\\s+stacklineSku\\s*=\\s*"${pinnedStacklineSku}"(?:\\s+question\\s*=\\s*"[^"]*")?\\s*/?>`,
      "g"
    );
    return conversationState.streamingAIResponse.replace(pinnedProductTagRegex, "").trim();
  }, [conversationState.streamingAIResponse, pinnedStacklineSku]);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Toaster, {}),
    /* @__PURE__ */ jsxs(
      "div",
      {
        className: "concierge-root",
        style: {
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
          maxHeight: "100%",
          overflow: "hidden"
        },
        children: [
          /* @__PURE__ */ jsx(
            ConciergeHeaderWidget,
            {
              showMaximize: showMaximize && !!conversationState.conversationId,
              isMaximized,
              onMaximize: handleMaximizeClick,
              onMinimize,
              onClose: widgetState.close,
              onHistoryClick: handleHistoryClick,
              title
            }
          ),
          /* @__PURE__ */ jsx(DevOptionsPanel, { ...devOpts }),
          /* @__PURE__ */ jsx(PinnedProductContext, { pageContext, product: pinnedProduct, isLoading: isPinnedProductLoading }),
          /* @__PURE__ */ jsx(
            ConciergeChatAreaV2,
            {
              appName: config == null ? void 0 : config.appName,
              brandId,
              conversationId: conversationState.conversationId,
              isEmbedded: true,
              apiBaseUrl: conversationState.apiBaseUrl,
              pageContext,
              conversationHistory: filteredConversationHistory,
              productMetadataMap: conversationState.productMetadataMap,
              surveyMetadataMap: conversationState.surveyMetadataMap,
              streamingProductGroups: conversationState.streamingProductGroups,
              previewSearchQueries: conversationState.previewSearchQueries,
              previewQueryProducts: conversationState.previewQueryProducts,
              followUpQuestions: conversationState.followUpQuestions,
              streamingFeaturedProduct: filteredStreamingFeaturedProduct,
              streamingStoreLocator: conversationState.streamingStoreLocator,
              streamingLoginRequired: conversationState.streamingLoginRequired,
              streamingDevInfo: conversationState.streamingDevInfo,
              onSignIn: wrappedOnSignIn,
              isActivelyStreaming: conversationState.isActivelyStreaming,
              streamingAIResponse: filteredStreamingAIResponse,
              currentStatus: conversationState.currentStatus,
              isWaitingForFollowUpQueries: conversationState.isWaitingForFollowUpQueries,
              isLoadingConversationQuery,
              handleSearch,
              onUpdateFeedback: handleUpdateFeedback,
              onVariantSelect,
              onTrackAccessPoint: handleTrackAccessPoint,
              searchInputValue,
              setSearchInputValue,
              isSharedView: conversationState.isSharedView,
              sharedByUserName: conversationState.sharedByUserName,
              sourceConversationId: conversationState.sourceConversationId,
              sourceCheckpointIndex: conversationState.sourceCheckpointIndex
            }
          )
        ]
      }
    )
  ] });
};
const ConciergeWidgetChat = (props) => {
  return /* @__PURE__ */ jsx(ConciergeWidgetChatContent, { ...props });
};
export {
  ConciergeWidgetChat
};
//# sourceMappingURL=ConciergeWidgetChat.js.map
